import React, { useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Stack,
  Chip,
  useTheme,
  useMediaQuery,
  Fade
} from '@mui/material';
import { 
  ArrowForward, 
  Security, 
  Speed, 
  Palette,
  Star,
  CheckCircle,
  Shield
} from '@mui/icons-material';
import { useLanguage } from '../../../localization';
import tokens from '../../../theme/tokens';

const Hero = ({ onGetStarted }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const mockupRef = useRef(null);
  const { t } = useLanguage();

  // 3D mouse tracking effect for desktop
  const handleMouseMove = (e) => {
    if (isMobile || !mockupRef.current) return;
    const { clientX, clientY } = e;
    const { innerWidth, innerHeight } = window;
    const moveX = (clientX - innerWidth / 2) / 50;
    const moveY = (clientY - innerHeight / 2) / 50;
    mockupRef.current.style.transform = `perspective(1000px) rotateY(${moveX}deg) rotateX(${-moveY}deg)`;
  };

  useEffect(() => {
    if (isMobile) return; // Skip effect for mobile devices
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isMobile, handleMouseMove]);

  const renderHighlightedText = (text, highlightWord) => {
    const parts = text.split(highlightWord);
    
    if (parts.length === 1) {
      return text;
    }
    
    return (
      <>
        {parts[0]}
        <span style={{ color: tokens.colors.primary.main }}>{highlightWord}</span>
        {parts[1]}
      </>
    );
  };

  const features = [
    { icon: <Security />, text: t('hero.features.alwaysPrivate') },
    { icon: <Speed />, text: t('hero.features.readyInMinutes') },
    { icon: <Palette />, text: t('hero.features.beautifulTemplates') }
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${tokens.colors.primary.main}15 0%, ${tokens.colors.secondary.main}10 100%)`,
        pt: { 
          xs: `calc(${tokens.components.header.height} + 2rem)`, // Account for fixed header + extra space
          md: `calc(${tokens.components.header.height} + 3rem)` 
        },
        pb: { xs: 4, md: 8 },
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center'
      }}
      onMouseMove={!isMobile ? handleMouseMove : undefined}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          <Grid item xs={12} lg={6}>
            <Box sx={{ 
              maxWidth: { xs: '100%', lg: '600px' },
              textAlign: { xs: 'center', lg: 'left' }
            }}>
              {/* Privacy Badge */}
              <Fade in timeout={800}>
                <Box sx={{ display: 'flex', justifyContent: { xs: 'center', lg: 'flex-start' }, mb: { xs: 3, md: 4 } }}>
                  <Chip
                    icon={
                      <Box
                        sx={{
                          backgroundColor: tokens.colors.primary.main,
                          borderRadius: '50%',
                          p: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Shield sx={{ fontSize: 16, color: 'white' }} />
                      </Box>
                    }
                    label={`${t('hero.badge.madeInEu')} • ${t('hero.badge.privateSecure')}`}
                    sx={{
                      backgroundColor: 'white',
                      border: `1px solid ${tokens.colors.borders.light}`,
                      color: tokens.colors.text.secondary,
                      fontWeight: tokens.typography.fontWeights.medium,
                      pl: 1,
                      pr: 2,
                      py: 0.5,
                      boxShadow: tokens.shadows.sm,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      '& .MuiChip-icon': {
                        ml: 0
                      }
                    }}
                  />
                </Box>
              </Fade>

              {/* Main Headline */}
              <Fade in timeout={1000}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { 
                      xs: '1.75rem', 
                      sm: '2.25rem', 
                      md: '2.75rem', 
                      lg: '3.25rem',
                      xl: '3.5rem'
                    },
                    fontWeight: tokens.typography.fontWeights.bold,
                    color: tokens.colors.text.primary,
                    lineHeight: { xs: 1.2, md: 1.1 },
                    mb: { xs: 2, md: 3 },
                    letterSpacing: '-0.02em'
                  }}
                >
                  {renderHighlightedText(
                    t('hero.headline'),
                    t('hero.highlightWord')
                  )}
                </Typography>
              </Fade>

              {/* Subtitle */}
              <Fade in timeout={1200}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                    color: tokens.colors.text.secondary,
                    lineHeight: 1.6,
                    mb: { xs: 3, md: 4 },
                    fontWeight: tokens.typography.fontWeights.normal,
                    px: { xs: 1, sm: 0 }
                  }}
                >
                  {t('hero.subtitle')}
                </Typography>
              </Fade>

              {/* Feature highlights - Hidden on small mobile */}
              {!isSmallMobile && (
                <Fade in timeout={1300}>
                  <Box sx={{ 
                    display: 'flex',
                    justifyContent: { xs: 'center', lg: 'flex-start' },
                    mb: { xs: 3, md: 4 }
                  }}>
                    <Stack 
                      direction={{ xs: 'column', sm: 'row' }} 
                      spacing={1} 
                      sx={{ 
                        alignItems: 'center',
                        gap: { xs: 1, sm: 2 }
                      }}
                    >
                      {features.map((feature, index) => (
                        <Chip
                          key={index}
                          icon={feature.icon}
                          label={feature.text}
                          sx={{
                            backgroundColor: tokens.colors.alpha.white[900],
                            border: `1px solid ${tokens.colors.borders.light}`,
                            color: tokens.colors.text.primary,
                            fontWeight: tokens.typography.fontWeights.medium,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            '& .MuiChip-icon': {
                              color: tokens.colors.primary.main
                            }
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Fade>
              )}

              {/* CTA Buttons */}
              <Fade in timeout={1400}>
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={2} 
                  sx={{ 
                    pt: 2,
                    alignItems: 'center',
                    justifyContent: { xs: 'center', lg: 'flex-start' }
                  }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={onGetStarted}
                    endIcon={<ArrowForward />}
                    sx={{
                      backgroundColor: tokens.colors.primary.main,
                      color: 'white',
                      fontWeight: tokens.typography.fontWeights.semibold,
                      textTransform: 'none',
                      px: { xs: 3, sm: 4 },
                      py: { xs: 1.25, sm: 1.5 },
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      borderRadius: tokens.radius.base,
                      boxShadow: tokens.shadows.md,
                      width: { xs: '100%', sm: 'auto' },
                      maxWidth: { xs: '280px', sm: 'none' },
                      '&:hover': {
                        backgroundColor: tokens.colors.primary.dark,
                        boxShadow: tokens.shadows.highlight,
                        transform: 'translateY(-1px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {t('hero.cta.primary')}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      const element = document.querySelector('#how-it-works');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    sx={{
                      color: tokens.colors.text.primary,
                      borderColor: tokens.colors.borders.default,
                      fontWeight: tokens.typography.fontWeights.medium,
                      textTransform: 'none',
                      px: { xs: 3, sm: 4 },
                      py: { xs: 1.25, sm: 1.5 },
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      borderRadius: tokens.radius.base,
                      width: { xs: '100%', sm: 'auto' },
                      maxWidth: { xs: '280px', sm: 'none' },
                      '&:hover': {
                        backgroundColor: tokens.colors.neutral[50],
                        borderColor: tokens.colors.primary.main,
                        color: tokens.colors.primary.main
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {t('hero.cta.secondary')}
                  </Button>
                </Stack>
              </Fade>
            </Box>
          </Grid>

          {/* Resume Mockup - Desktop Only */}
          {!isMobile && (
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1600}>
                <Box sx={{ 
                  position: 'relative',
                  ml: { lg: 3, xl: 4 }, // Add left margin for spacing from text content
                  mt: { lg: 2 }, // Add top margin for vertical spacing
                  mb: { lg: 2 } // Add bottom margin for vertical spacing
                }}>
                  <Box
                    ref={mockupRef}
                    sx={{
                      position: 'relative',
                      transformStyle: 'preserve-3d',
                      transition: 'transform 0.2s ease-out',
                      // A4 proportions: 210mm × 297mm (aspect ratio 1:1.414)
                      width: '100%',
                      maxWidth: { lg: '420px', xl: '480px' }, // Realistic A4 width
                      aspectRatio: '210 / 297', // Proper A4 aspect ratio
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: 'white',
                        borderRadius: tokens.radius.xl,
                        boxShadow: tokens.shadows.elegant,
                        overflow: 'hidden',
                        border: `1px solid ${tokens.colors.borders.light}`,
                        width: '100%',
                        height: '100%', // Fill the aspect ratio container
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      {/* Browser Header */}
                      <Box
                        sx={{
                          backgroundColor: tokens.colors.neutral[100],
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          borderBottom: `1px solid ${tokens.colors.borders.light}`
                        }}
                      >
                        <Box sx={{ width: 12, height: 12, backgroundColor: '#ef4444', borderRadius: '50%' }} />
                        <Box sx={{ width: 12, height: 12, backgroundColor: '#f59e0b', borderRadius: '50%' }} />
                        <Box sx={{ width: 12, height: 12, backgroundColor: '#10b981', borderRadius: '50%' }} />
                      </Box>

                      {/* Resume Content */}
                      <Box sx={{ 
                        p: 3,
                        flex: 1,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2.5
                      }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: tokens.colors.text.primary, mb: 0.5 }}>
                              Jonathan Smith
                            </Typography>
                            <Typography sx={{ color: tokens.colors.primary.main, fontWeight: 'medium', mb: 1.5 }}>
                              Senior Product Designer
                            </Typography>
                            
                            <Box sx={{ fontSize: '0.75rem', color: tokens.colors.text.secondary, lineHeight: 1.4 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <Box sx={{ color: tokens.colors.primary.main, mr: 1 }}>✉</Box>
                                jonathan.smith@example.com
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <Box sx={{ color: tokens.colors.primary.main, mr: 1 }}>☏</Box>
                                (+1) 555-123-4567
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ color: tokens.colors.primary.main, mr: 1 }}>📍</Box>
                                San Francisco, CA
                              </Box>
                            </Box>
                          </Box>
                          
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                backgroundColor: tokens.colors.primary.light + '20',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: tokens.colors.primary.main,
                                fontSize: '1.2rem',
                                fontWeight: 'bold',
                                mb: 1.5
                              }}
                            >
                              JS
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Box sx={{ width: 16, height: 16, backgroundColor: tokens.colors.neutral[300], borderRadius: 1 }} />
                              <Box sx={{ width: 16, height: 16, backgroundColor: tokens.colors.neutral[300], borderRadius: 1 }} />
                              <Box sx={{ width: 16, height: 16, backgroundColor: tokens.colors.neutral[300], borderRadius: 1 }} />
                            </Box>
                          </Box>
                        </Box>

                        {/* Professional Profile */}
                        <Box>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold', color: tokens.colors.text.primary, mb: 1, textTransform: 'uppercase' }}>
                            Professional Profile
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: tokens.colors.text.secondary, lineHeight: 1.5 }}>
                            Award-winning product designer with 8+ years of experience creating user-centered digital 
                            experiences for high-growth companies. Specialized in design systems, user research, and 
                            leading cross-functional teams to deliver cohesive products that delight users and drive business 
                            growth. Passionate about accessible design and mentoring junior designers.
                          </Typography>
                        </Box>

                        {/* Experience */}
                        <Box>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold', color: tokens.colors.text.primary, mb: 1, textTransform: 'uppercase', display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ color: tokens.colors.primary.main, mr: 1 }}>📈</Box>
                            Experience
                          </Typography>
                          
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: tokens.colors.text.primary }}>
                                Lead Product Designer
                              </Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: tokens.colors.primary.main }}>
                                2020 - Present
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.75rem', color: tokens.colors.primary.main, mb: 0.5 }}>
                              DesignCraft Inc.
                            </Typography>
                            <Box sx={{ fontSize: '0.7rem', color: tokens.colors.text.secondary, pl: 2 }}>
                              • Led the redesign of the company's flagship product, increasing user engagement by 46%
                            </Box>
                          </Box>

                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: tokens.colors.text.primary }}>
                                Senior UX Designer
                              </Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: tokens.colors.primary.main }}>
                                2017 - 2020
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.75rem', color: tokens.colors.primary.main }}>
                              TechInnovate Co.
                            </Typography>
                          </Box>
                        </Box>

                        {/* Education */}
                        <Box>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold', color: tokens.colors.text.primary, mb: 1, textTransform: 'uppercase', display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ color: tokens.colors.primary.main, mr: 1 }}>🎓</Box>
                            Education
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: tokens.colors.text.primary }}>
                              MSc Interaction Design
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: tokens.colors.primary.main }}>
                              2015 - 2017
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: '0.75rem', color: tokens.colors.primary.main }}>
                            California Institute of Design
                          </Typography>
                        </Box>

                        {/* Skills */}
                        <Box>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold', color: tokens.colors.text.primary, mb: 1, textTransform: 'uppercase', display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ color: tokens.colors.primary.main, mr: 1 }}>🛠</Box>
                            Skills
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {['Figma', 'UI/UX Design', 'Design Systems', 'User Research', 'Prototyping', 'Adobe CC'].map((skill) => (
                              <Chip
                                key={skill}
                                label={skill}
                                size="small"
                                sx={{
                                  backgroundColor: tokens.colors.primary.light + '20',
                                  color: tokens.colors.primary.main,
                                  fontSize: '0.65rem',
                                  height: 20,
                                  '& .MuiChip-label': {
                                    px: 1
                                  }
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Background Decorations */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -24,
                      right: -24,
                      width: 128,
                      height: 128,
                      background: `radial-gradient(circle, ${tokens.colors.primary.light}20, transparent)`,
                      borderRadius: '50%',
                      filter: 'blur(40px)',
                      zIndex: -1
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -24,
                      left: -24,
                      width: 128,
                      height: 128,
                      background: `radial-gradient(circle, ${tokens.colors.secondary.main}20, transparent)`,
                      borderRadius: '50%',
                      filter: 'blur(40px)',
                      zIndex: -1
                    }}
                  />
                </Box>
              </Fade>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default Hero;